import queryString from 'query-string';
import createRequest from 'modules/api/request';
import { URLS, HTTP_METHODS, HEADERS_CONTENT_TYPE } from 'modules/api/constants';
import { DealType } from 'components/common/types/Deal.types';
import { isCreatedAfterSummaryPanelChanges } from 'components/pages/DealWithLines/DealLines/transformDealDetails';

const defaultBids = {
  bidRequest: 0,
  bidResponse: 0,
  wonImpressions: 0,
  bookedImpressions: 0,
  soldImpressions: 0,
  fillRate: 0,
};

const getFilterRequestData = (filters = {}) => {
  const requestData = {};

  if (filters.dsp?.code) {
    requestData.dspCode = filters.dsp.code;
  }

  if (filters?.dismountedSearch) {
    requestData.dismountedSearch = filters.dismountedSearch;
  }

  if (filters.dealNameOrId) {
    requestData.dealNameOrId = filters.dealNameOrId;
  }

  if (filters.advertiser?.code) {
    requestData.advertiserName = filters.advertiser.name;
  }

  if (filters.status) {
    requestData.bookingStatusCode = filters.status;
  }

  if (filters.internalId) {
    requestData.internalId = filters.internalId;
  }

  if (filters.dealType) {
    requestData.dealType = filters.dealType;
  }

  return requestData;
};

const assignAllocatedImpressions = (deals) => {
  return deals.map((deal) => {
    const {
      dealTypeCode,
      totalImpressions,
      bids: { bookedImpressions: reserved },
    } = deal;

    if (dealTypeCode !== DealType.GUARANTEED || !reserved) {
      return deal;
    }

    const sold = Number(totalImpressions);

    return {
      ...deal,
      allocatedImpressions: {
        sold,
        reserved,
        pacing: sold / reserved,
      },
    };
  });
};

const fetchDealsBids = async (cancelFunctions, deals) => {
  const { send, cancel: cancelSend } = createRequest({
    url: URLS().DEAL_LIST_BIDS_DATA_URL(),
    method: HTTP_METHODS.POST,
    data: JSON.stringify({
      dealId: deals.map((item) => item.dealId),
    }),
    showLoader: true,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
  });

  cancelFunctions.DEAL_LIST_BIDS_BULK = cancelSend;

  const result = await send();
  const newBids = result.data;

  return deals.map((deal) => {
    const dealBids = newBids[deal.dealId] ? Object.values(newBids[deal.dealId])[0] : { ...defaultBids };
    const dealData = {
      ...deal,
      bids: dealBids,
    };

    if (deal.dealTypeCode !== DealType.GUARANTEED || !dealBids.bookedImpressions) return dealData;

    const soldImpressions = Number(deal.totalImpressions);

    return {
      ...dealData,
      remainingImpressions: dealBids.bookedImpressions - soldImpressions,
    };
  });
};

export const getMultiLineDeals = async (cancelFunctions, filters = {}, { page, size, sortBy, sortDir }, cancelName) => {
  const query = queryString.stringify(
    { ...getFilterRequestData(filters), page, size, sortBy, sortDir },
    { skipEmptyString: true, arrayFormat: 'comma' },
  );

  const { send, cancel } = createRequest({
    url: URLS().GET_DEALS(query),
    method: HTTP_METHODS.GET,
    showLoader: true,
  });

  if (cancelFunctions[cancelName]) cancelFunctions[cancelName]();
  cancelFunctions[cancelName] = cancel;

  return send();
};

export const getDealsWithBids = async ({ cancelFunctions, filters = {}, page, size, sort, cancelName }) => {
  const { sortBy, sortDir } = sort;
  const result = await getMultiLineDeals(cancelFunctions, filters, { page, size, sortBy, sortDir }, cancelName);

  const dealsWithDefaultBids = result.data.deals.content.map((deal) => ({
    ...deal,
    internalId: deal.internalId.toString(),
    bids: {
      ...defaultBids,
    },
  }));

  const dealsWithBids = await fetchDealsBids(cancelFunctions, dealsWithDefaultBids);
  const dealsWithAllocatedImpressions = assignAllocatedImpressions(dealsWithBids);

  return {
    ...result.data.deals,
    content: dealsWithAllocatedImpressions,
  };
};

export const getDealLines = async ({ cancelFunctions, dealId }) => {
  const { send, cancel } = createRequest({
    url: URLS().FETCH_DEAL(dealId),
    method: HTTP_METHODS.GET,
    showLoader: false,
  });

  if (cancelFunctions.FETCH_DEAL_LINES) cancelFunctions.FETCH_DEAL_LINES();
  cancelFunctions.FETCH_DEAL_LINES = cancel;

  const result = await send();
  let dealResponse = result.data;

  if (isCreatedAfterSummaryPanelChanges({ dealType: dealResponse.dealTypeCode, lines: dealResponse.dealLines })) {
    dealResponse = {
      ...dealResponse,
      dealLines: dealResponse.dealLines.map((dealLine) => ({
        ...dealLine,
        totalImpressions: dealLine.lineImpressions?.aggregatedImpressions ?? dealLine.totalImpressions,
      })),
    };
  }

  return (dealResponse.dealLines || []).map((line) => ({
    ...line,
    advertiserName: dealResponse.advertiserName,
    agencyName: dealResponse.agencyName,
    allocatedImpressions: line.allocatedImpressions,
    availableImpressions: line.availableImpressions,
    bookingStatusCode: line.bookingStatusCode,
    brandName: dealResponse.brandName,
    cpm: line.floorCPM || line.fixedCPM,
    dealId: line.lineId,
    dealName: line.dealName || line.lineName || `Line ${line.lineId || line.internalId}`,
    dealTypeCode: dealResponse.dealTypeCode,
    dspName: dealResponse.dspName,
    endDate: line.endDate,
    floorCPM: line.floorCPM,
    productCategoryName: dealResponse.productCategoryName,
    startDate: line.startDate,
    totalImpressions: line.totalImpressions,
  }));
};
